import { createFileRoute } from "@tanstack/react-router";
import { SchemaField } from "@papier/grapesjs-editor/src/components/editor/schema-builder/types/schema";
import { lazy, Suspense } from "react";

const UmoEditor = lazy(() =>
  import("@/components/editors/UmoEditor").then((m) => ({ default: m.UmoEditor })),
);

export const Route = createFileRoute("/umo")({
  component: () => (
    <Suspense
      fallback={
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100vh",
            fontSize: "16px",
            color: "#666",
          }}
        >
          Loading UMO Editor...
        </div>
      }
    >
      <RouteComponent />
    </Suspense>
  ),
});

const schema: SchemaField[] = [
  {
    id: "1",
    name: "title",
    type: "string",
    required: true,
    description: "The main title of the document",
  },
  {
    id: "2",
    name: "author",
    type: "object",
    required: true,
    description: "Author information",
    objectFields: [
      {
        id: "2-1",
        name: "firstName",
        type: "string",
        required: true,
        description: "Author's first name",
        parentId: "2",
        parentType: "object" as const,
      },
      {
        id: "2-2",
        name: "lastName",
        type: "string",
        required: true,
        description: "Author's last name",
        parentId: "2",
        parentType: "object" as const,
      },
      {
        id: "2-3",
        name: "email",
        type: "string",
        required: false,
        description: "Author's email address",
        parentId: "2",
        parentType: "object" as const,
      },
    ],
  },
  {
    id: "3",
    name: "publishedDate",
    type: "date",
    required: false,
    description: "Date when the document was published",
  },
  {
    id: "4",
    name: "status",
    type: "enum",
    required: true,
    description: "Current status of the document",
    enumValues: ["draft", "in-review", "published", "archived"],
  },
  {
    id: "5",
    name: "tags",
    type: "array",
    required: false,
    description: "List of tags for categorization2",
    arrayItemType: {
      id: "5-item",
      name: "tag",
      type: "string",
      required: true,
      parentId: "5",
      parentType: "array" as const,
      description: "A single tag",
    },
  },
  {
    id: "6",
    name: "isPublic",
    type: "boolean",
    required: true,
    description: "Whether the document is publicly accessible",
  },
  {
    id: "7",
    name: "comments",
    type: "array",
    required: false,
    description: "List of comments on the document",
    arrayItemType: {
      id: "7-item",
      name: "comment",
      type: "object",
      required: true,
      parentId: "7",
      parentType: "array" as const,
      objectFields: [
        {
          id: "7-item-1",
          name: "author",
          type: "string",
          required: true,
          description: "Comment author's name",
          parentId: "7-item",
          parentType: "object" as const,
        },
        {
          id: "7-item-2",
          name: "content",
          type: "string",
          required: true,
          description: "Content of the comment",
          parentId: "7-item",
          parentType: "object" as const,
        },
        {
          id: "7-item-3",
          name: "timestamp",
          type: "date",
          required: true,
          description:
            "Timestamp when the comment was made, in ISO 8601 format (e.g., 2023-10-01T12:00:00Z)",
          parentId: "7-item",
          parentType: "object" as const,
        },
      ],
    },
  },
];

// Component that mounts UMO editor with the given height

function RouteComponent() {
  console.log("[Route Debug] Rendering UmoEditor with disableAI=true");

  const handleContentUpdate = (content: string) => {
    console.log("[Route Debug] Content updated:", content);
  };

  // Simple initial content
  const initialContent = `<h1>Welcome to UMO Editor</h1>
<p>This is the initial content for the editor.</p>
<p>You can start editing here...</p>
<ul>
  <li>AI Assistant is disabled</li>
  <li>Content updates are tracked</li>
  <li>Initial content is loaded</li>
</ul>`;

  return (
    <div style={{ position: "relative", height: "100vh" }}>
      {/* Debug info overlay */}
      {/* <div style={{
        position: "absolute",
        top: 10,
        right: 10,
        zIndex: 1000,
        background: "rgba(0,0,0,0.8)",
        color: "white",
        padding: "10px",
        borderRadius: "5px",
        fontSize: "12px",
        maxWidth: "300px"
      }}>
        <div><strong>Debug Info:</strong></div>
        <div>Content Length: {editorContent.length} chars</div>
        <div>Last Update: {lastUpdateTime ? lastUpdateTime.toLocaleTimeString() : "Never"}</div>
        <div>AI Disabled: ✅</div>
      </div> */}

      <UmoEditor
        schema={{ id: "schema1", name: "Document Schema", schema: schema }}
        onSchemaChange={() => {}}
        disableSave
        disableAI={true}
        content={initialContent}
        onUpdate={handleContentUpdate}
      />
    </div>
  );
}
